import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ASSETS } from "../assets";
import { DISCORD_URL } from "../constants/links";
import { getDiscordEvents } from "../lib/discord-api";
import { Reveal } from "./Reveal";

const STEPS = [
  "Připoj se na náš Discord",
  "Přečti si pravidla a setting",
  "Vytvoř si účet",
  "Stáhni klienta WoW 3.3.5a",
  "Stáhni Draconia Launcher",
  "Vytvoř si postavu",
  "Přihlas se a začni svůj příběh",
];

const EVENT_TYPE_LABELS = {
  external: "Veřejná událost",
  voice: "Voice akce",
  stage: "Stage akce",
};

const PRAGUE_TZ = "Europe/Prague";

const formatEventMonth = (isoDate) =>
  new Intl.DateTimeFormat("cs-CZ", { month: "short", timeZone: PRAGUE_TZ })
    .format(new Date(isoDate))
    .replace(".", "")
    .toUpperCase();

const formatEventDay = (isoDate) =>
  new Intl.DateTimeFormat("cs-CZ", { day: "2-digit", timeZone: PRAGUE_TZ })
    .format(new Date(isoDate))
    .replace(".", "");

const formatEventTime = (isoDate) =>
  new Intl.DateTimeFormat("cs-CZ", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: PRAGUE_TZ }).format(
    new Date(isoDate)
  );

const formatEventDate = (isoDate) => {
  const month = formatEventMonth(isoDate);
  const capitalized = month.charAt(0) + month.slice(1).toLowerCase();
  return `${formatEventDay(isoDate)}. ${capitalized}`;
};

const cardBase =
  "rounded-xl border border-white/10 bg-[#17171b] p-8";

const Heading = ({ children }) => (
  <p className="mb-6 font-display text-xs font-600 uppercase tracking-[0.28em] text-[#F0801F]">
    {children}
  </p>
);

export const StartGrid = () => {
  const [events, setEvents] = useState(null);

  useEffect(() => {
    getDiscordEvents()
      .then(setEvents)
      .catch(() => setEvents([]));
  }, []);

  return (
    <section
      className="relative scroll-mt-20 px-6 py-10"
      data-testid="start-grid-section"
    >
      <div className="mx-auto grid max-w-[1584px] grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Getting started */}
        <Reveal className={cardBase} data-testid="getting-started-card">
          <Heading>Jak začít</Heading>
          <ol className="space-y-4">
            {STEPS.map((s, i) => (
              <li key={s} className="flex items-center gap-4">
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-[#F0801F]/40 font-display text-xs text-[#F0801F]">
                  {i + 1}
                </span>
                <span className="text-sm text-white/70">{s}</span>
              </li>
            ))}
          </ol>
          <Link
            to="/jak-hrat"
            data-testid="start-howto-btn"
            className="mt-8 block w-full rounded-md bg-[#F0801F] py-3 text-center font-display text-sm font-600 uppercase tracking-[0.12em] text-[#141417] transition-colors hover:bg-[#ff9438]"
          >
            Jak hrát
          </Link>
        </Reveal>

        {/* Forgotten Realms */}
        <Reveal delay={0.1} className={cardBase} data-testid="lore-card">
          <Heading>Forgotten Realms</Heading>
          <div className="overflow-hidden rounded-lg border border-white/10">
            <img
              src={ASSETS.realmsCity}
              alt="Fantasy město ve světě Forgotten Realms na WoW RolePlay serveru Draconia RP"
              loading="lazy"
              width={1264}
              height={711}
              className="h-44 w-full object-cover"
            />
          </div>
          <p className="mt-6 text-sm leading-relaxed text-white/60">
            Náš server je zasazen do Forgotten Realms. Objevuj známá místa,
            odhaluj nová tajemství a utvářej osud Faerûnu.
          </p>
          <Link
            to="/lore"
            data-testid="explore-lore-link"
            className="group mt-6 inline-flex items-center gap-2 font-display text-sm font-600 uppercase tracking-[0.12em] text-[#F0801F]"
          >
            Prozkoumat lore
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>

        {/* Upcoming events (live from Discord Scheduled Events) */}
        <Reveal delay={0.2} className={cardBase} data-testid="events-card">
          <Heading>Nadcházející události</Heading>
          <div className="max-h-[360px] space-y-3 overflow-y-auto pr-1 [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#F0801F]/40 [&::-webkit-scrollbar-track]:bg-transparent">
            {events === null && <p className="text-sm text-white/40">Načítám akce…</p>}
            {events?.length === 0 && (
              <p className="text-sm text-white/45">Zatím nejsou naplánované žádné akce - sleduj náš Discord.</p>
            )}
            {events?.map((e) => (
              <a
                key={e.id}
                href={e.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="discord-event-row"
                className="flex items-start gap-4 rounded-lg border border-white/10 p-3 transition-colors hover:border-[#F0801F]/40"
              >
                {e.image_url ? (
                  <img
                    src={e.image_url}
                    alt=""
                    loading="lazy"
                    className="h-14 w-14 shrink-0 rounded-md border border-white/10 object-cover"
                  />
                ) : (
                  <div className="grid h-14 w-14 shrink-0 place-items-center rounded-md border border-[#F0801F]/30 bg-[#F0801F]/5 leading-none">
                    <span className="font-display text-[10px] uppercase tracking-widest text-[#F0801F]">
                      {formatEventMonth(e.start_time)}
                    </span>
                    <span className="font-display text-xl font-600 text-white">
                      {formatEventDay(e.start_time)}
                    </span>
                  </div>
                )}
                <div className="min-w-0">
                  <p className="font-display text-sm font-600 uppercase tracking-[0.06em] text-white">
                    {e.name}
                  </p>
                  {e.description && (
                    <p className="mt-1 line-clamp-2 text-xs text-white/50">{e.description}</p>
                  )}
                  <p className="mt-1 text-xs text-white/45">
                    {EVENT_TYPE_LABELS[e.type] ?? "Událost"} · {formatEventDate(e.start_time)} · {formatEventTime(e.start_time)}
                    {e.location ? ` · ${e.location}` : ""}
                  </p>
                </div>
              </a>
            ))}
          </div>
          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="view-calendar-link"
            className="group mt-6 inline-flex items-center gap-2 font-display text-sm font-600 uppercase tracking-[0.12em] text-[#F0801F]"
          >
            Zobrazit kalendář
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>
        </Reveal>
      </div>
    </section>
  );
};
